import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Box, Grid } from '@mui/material';
import moment from 'moment/moment';

import TextFieldFormik from '../../../../../common/text_field';
import { SubmitButton } from '../../../../../common/button';
import { getAutomaticExpirationPromotionSettingType } from '../../../../../common/utils/automatic_expiration_promotion_setting_utils';
import { parseDateString } from '../../../../../../core/utils/date_utils';
import { getAutomaticExpirationPromotionSettingAction, getAutomaticExpirationPromotionSettingState } from '../../../../../../features/price/get_automatic_expiration_promotion_setting';
import useNotification from '../../../../../../core/hooks/use_notification';
import TimePickerFormik from '../../../../../common/time_picker';
import { updateAutomaticExpirationPromotionSettingAction, updateAutomaticExpirationPromotionSettingState } from '../../../../../../features/price/update_automatic_expiration_promotion_setting';
import { MISC_DATE_ISO_TIME_ONLY_FORMAT } from '../../../../../../core/constants';
import { getAutomaticExpirationPromotionSettingNotificationData, updateAutomaticExpirationPromotionSettingNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import SectionPaper from '../../../../../common/section_paper';
import BottomActionBar from '../../../../../common/bottom_action_bar';

const AutomaticExpirationPromotionSettingForm = ({ id }) => {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const getAutomaticExpirationPromotionSetting = useSelector(getAutomaticExpirationPromotionSettingState);
    const updateAutomaticExpirationPromotionSetting = useSelector(updateAutomaticExpirationPromotionSettingState);

    const initialValues = {
        type: getAutomaticExpirationPromotionSettingType(getAutomaticExpirationPromotionSetting?.data?.type),
        name: getAutomaticExpirationPromotionSetting?.data?.name || '',
        discount: getAutomaticExpirationPromotionSetting?.data?.discount || '',
        expirationPromotionCreationTime: getAutomaticExpirationPromotionSetting?.data?.expirationPromotionCreationTime
            ? moment(getAutomaticExpirationPromotionSetting?.data?.expirationPromotionCreationTime, MISC_DATE_ISO_TIME_ONLY_FORMAT)
            : ''
    };

    const schema = Yup.object().shape({
        type: Yup.string().required(),
        name: Yup.string().required(),
        discount: Yup.number()
            .required()
            .min(0.01)
            .max(100.00),
        expirationPromotionCreationTime: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required(),
    });

    const onSubmit = (values) => {
        const expirationPromotionCreationTime = values?.expirationPromotionCreationTime?.format(MISC_DATE_ISO_TIME_ONLY_FORMAT);

        const form = {
            name: values?.name,
            discount: values?.discount,
            expirationPromotionCreationTime
        };

        dispatch(updateAutomaticExpirationPromotionSettingAction({ form, id }))
            .then(response => {
                showNotification(updateAutomaticExpirationPromotionSettingNotificationData(response));

                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && dispatch(getAutomaticExpirationPromotionSettingAction(id))
                    .then(response => showErrorMessage(getAutomaticExpirationPromotionSettingNotificationData(response)));
            });
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }>
            { () => (
                <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <SectionPaper title="Zasady promocji">
                            <Grid container spacing={ 3 }>
                                <Grid item xs={ 12 } md={ 6 }>
                                    <TextFieldFormik name="type" label="Typ promocji" disabled/>
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 }>
                                    <TextFieldFormik name="name" label="Nazwa promocji"/>
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 }>
                                    <TextFieldFormik name="discount" label="Obniżka (%)" type="number" required
                                                     inputProps={ { min: '0.01', max: '100.00', step: '0.01' } }/>
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 }>
                                    <TimePickerFormik
                                        name="expirationPromotionCreationTime"
                                        required
                                        label="Godzina promocji"
                                        InputLabelProps={ { shrink: true } }
                                        views={ ['hours'] }
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </SectionPaper>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ { display: 'flex', justifyContent: 'flex-end', width: '100%' } }>
                            <SubmitButton isLoading={ updateAutomaticExpirationPromotionSetting?.loading }>
                                Zapisz zmiany
                            </SubmitButton>
                        </Box>
                    </BottomActionBar>
                </Box>
            ) }
        </Formik>
    );
};

export default AutomaticExpirationPromotionSettingForm;